import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { embeddedTranslations } from '@/lib/translations';

type Lang = 'ar' | 'en';

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string, fallback?: string) => string;
  isLoading: boolean;
  dir: 'rtl' | 'ltr';
  dashboardMultiLangEnabled: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// ─── Module-level caches ───
let _featureFlagCache: { enabled: boolean; ts: number } | null = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    const saved = localStorage.getItem('app_lang');
    return (saved === 'en' ? 'en' : 'ar') as Lang;
  });
  // Use embedded translations directly — no DB fetch needed
  const [isLoading] = useState(false);
  const [dashboardMultiLangEnabled, setDashboardMultiLangEnabled] = useState(() => {
    if (_featureFlagCache && Date.now() - _featureFlagCache.ts < CACHE_TTL) return _featureFlagCache.enabled;
    return true;
  });
  const flagFetched = useRef(false);

  const setLang = useCallback((newLang: Lang) => {
    setLangState(newLang);
    localStorage.setItem('app_lang', newLang);
    document.documentElement.lang = newLang;
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  }, []);

  // Fetch multi_language feature flag from platform_branding - once per session
  useEffect(() => {
    if (flagFetched.current) return;
    if (_featureFlagCache && Date.now() - _featureFlagCache.ts < CACHE_TTL) {
      setDashboardMultiLangEnabled(_featureFlagCache.enabled);
      if (!_featureFlagCache.enabled && lang !== 'ar') setLang('ar');
      flagFetched.current = true;
      return;
    }
    flagFetched.current = true;
    const fetchFlag = async () => {
      const { data } = await supabase
        .from('platform_branding')
        .select('feature_flags')
        .limit(1)
        .maybeSingle();
      const flags = (data as any)?.feature_flags as Record<string, boolean> | null;
      const enabled = flags?.multi_language ?? true;
      _featureFlagCache = { enabled, ts: Date.now() };
      setDashboardMultiLangEnabled(enabled);
      if (!enabled && lang !== 'ar') {
        setLang('ar');
      }
    };
    fetchFlag();
  }, []);

  // Force Arabic when feature is disabled (only for dashboard, not public menu)
  useEffect(() => {
    const isPublicMenu = window.location.pathname.startsWith('/menu/');
    if (!dashboardMultiLangEnabled && lang !== 'ar' && !isPublicMenu) {
      setLang('ar');
    }
  }, [dashboardMultiLangEnabled, lang, setLang]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  const t = useCallback(
    (key: string, fallback?: string) => {
      return embeddedTranslations[lang]?.[key] || fallback || key;
    },
    [lang]
  );

  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isLoading, dir, dashboardMultiLangEnabled }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Fallback for HMR or edge cases where provider isn't ready
    return {
      lang: 'ar',
      setLang: () => {},
      t: (key: string, fallback?: string) => fallback || key,
      isLoading: false,
      dir: 'rtl',
      dashboardMultiLangEnabled: true,
    };
  }
  return context;
};
